import React, { useState, useEffect, useCallback } from 'react';
import { Mail, HardDrive, CheckCircle2, AlertCircle, Trash2, ShieldAlert, ArrowRightLeft, Palmtree, Info } from 'lucide-react';
import { ShieldCheck, Key, RefreshCw, Users, Plus, Lock } from './icons';
import { microsoftO365API } from '../services/api';
import { useToast } from '../contexts/ToastContext';

export default function MicrosoftO365Tab({ usuario }) {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    // Modal states
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [forceChangePass, setForceChangePass] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    // Alias state
    const [newAlias, setNewAlias] = useState('');

    // Forwarding state
    const [showForwardingModal, setShowForwardingModal] = useState(false);
    const [forwardingEmail, setForwardingEmail] = useState('');

    // Out of Office state
    const [showOooModal, setShowOooModal] = useState(false);
    const [oooMessage, setOooMessage] = useState('Estaré fuera de la oficina por vacaciones.');

    const userEmail = usuario?.email || usuario?.username || '';

    const loadDetails = useCallback(async () => {
        if (!userEmail) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await microsoftO365API.getUserDetails(userEmail);
            setData(res.data?.data || null);
            setIsConnected(res.data?.isConnected || false);
            setErrorMessage(res.data?.errorMessage || null);
        } catch (err) {
            console.error('Error al cargar datos de M365:', err);
            setErrorMessage('No se pudo conectar con el servicio Microsoft 365.');
        } finally {
            setLoading(false);
        }
    }, [userEmail]);

    useEffect(() => {
        loadDetails();
    }, [loadDetails]);

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (!newPassword || newPassword.length < 8) {
            showToast('Error', 'La contraseña debe tener al menos 8 caracteres', 'warning');
            return;
        }
        setActionLoading(true);
        try {
            const res = await microsoftO365API.changePassword({
                userPrincipalName: userEmail,
                newPassword,
                forceChange: forceChangePass
            });
            showToast('Éxito', res.data.message || 'Contraseña restablecida correctamente', 'success');
            setShowPasswordModal(false);
            setNewPassword('');
        } catch (err) {
            showToast('Error', err.response?.data?.message || 'No se pudo cambiar la contraseña', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleResetMfa = async (revokeSessions = true) => {
        setActionLoading(true);
        try {
            const res = await microsoftO365API.resetMfa({
                userPrincipalName: userEmail,
                revokeSessions
            });
            showToast('Éxito', res.data.message || 'MFA restablecido y sesiones revocadas', 'success');
        } catch (err) {
            showToast('Error', err.response?.data?.message || 'No se pudo restablecer MFA', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleAddAlias = async (e) => {
        e.preventDefault();
        if (!newAlias || !newAlias.trim()) return;
        setActionLoading(true);
        try {
            const aliasFormatted = newAlias.trim().toLowerCase();
            const res = await microsoftO365API.manageAliases({
                userPrincipalName: userEmail,
                action: 'add',
                alias: aliasFormatted
            });
            showToast('Éxito', res.data.message || 'Alias agregado correctamente', 'success');
            setNewAlias('');
            loadDetails();
        } catch (err) {
            showToast('Error', err.response?.data?.message || 'No se pudo agregar el alias', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRemoveAlias = async (aliasToRemove) => {
        setActionLoading(true);
        try {
            const res = await microsoftO365API.manageAliases({
                userPrincipalName: userEmail,
                action: 'remove',
                alias: aliasToRemove
            });
            showToast('Éxito', res.data.message || 'Alias eliminado correctamente', 'success');
            loadDetails();
        } catch (err) {
            showToast('Error', err.response?.data?.message || 'No se pudo eliminar el alias', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleConvertSharedMailbox = async () => {
        if (!window.confirm(`¿Estás seguro de convertir el buzón ${userEmail} a Buzón Compartido (Shared Mailbox)?`)) return;
        setActionLoading(true);
        try {
            const res = await microsoftO365API.convertSharedMailbox({ userPrincipalName: userEmail });
            showToast('Éxito', res.data.message || 'Buzón convertido a compartido', 'success');
            loadDetails();
        } catch (err) {
            showToast('Error', err.response?.data?.message || 'No se pudo convertir el buzón', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSaveForwarding = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            const res = await microsoftO365API.configureForwarding({
                userPrincipalName: userEmail,
                forwardingEmail
            });
            showToast('Éxito', res.data.message || 'Reenvío configurado correctamente', 'success');
            setShowForwardingModal(false);
            loadDetails();
        } catch (err) {
            showToast('Error', err.response?.data?.message || 'No se pudo configurar el reenvío', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSaveOoo = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            const res = await microsoftO365API.configureOutOfOffice({
                userPrincipalName: userEmail,
                enabled: true,
                message: oooMessage
            });
            showToast('Éxito', res.data.message || 'Respuesta de fuera de oficina configurada', 'success');
            setShowOooModal(false);
            loadDetails();
        } catch (err) {
            showToast('Error', err.response?.data?.message || 'No se pudo configurar la auto-respuesta', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 text-center bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Consultando datos de Microsoft 365 / Entra ID...</p>
            </div>
        );
    }

    // Sin datos reales (usuario inexistente en Microsoft 365, sin permisos, etc.): se muestra el motivo
    // en vez de datos de ejemplo.
    if (!data) {
        return (
            <div className="p-8 text-center bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <Info className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">No se pudo consultar Microsoft 365</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{errorMessage || 'Este usuario no tiene datos disponibles en Microsoft 365.'}</p>
            </div>
        );
    }

    const mb = data?.mailbox || {};
    const storageDisponible = mb.usagePercentage !== null && mb.usagePercentage !== undefined;
    const usagePct = storageDisponible ? mb.usagePercentage : 0;
    const usageGB = storageDisponible ? (mb.usageBytes / (1024 ** 3)).toFixed(1) : null;
    const quotaGB = storageDisponible ? (mb.quotaBytes / (1024 ** 3)).toFixed(0) : null;

    return (
        <div className="space-y-6">
            {/* Header del Estado de la Integración */}
            <div className="p-4 bg-gradient-to-r from-blue-900 to-indigo-900 text-white rounded-2xl shadow-md flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center space-x-3">
                    <div className="p-2.5 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                        <ShieldCheck className="w-6 h-6 text-blue-300" />
                    </div>
                    <div>
                        <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-lg text-white">Consola Microsoft 365 & Active Directory</h3>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${isConnected ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'}`}>
                                {isConnected ? '● Graph API Conectado' : '⚡ Modo Vista Previa / Esperando Consentimiento'}
                            </span>
                        </div>
                        <p className="text-xs text-blue-200 mt-0.5">
                            Gestiona contraseñas, licencias, MFA, alias y almacenamiento de buzón en tiempo real.
                        </p>
                    </div>
                </div>

                <button
                    onClick={loadDetails}
                    className="px-3.5 py-2 bg-white/10 hover:bg-white/20 text-xs font-semibold rounded-xl backdrop-blur-md border border-white/20 transition-all flex items-center space-x-1.5 shrink-0"
                >
                    <RefreshCw className="w-3.5 h-3.5" />
                    <span>Actualizar Datos</span>
                </button>
            </div>

            {/* Banner Informativo si falta consentimiento de Azure */}
            {errorMessage && (
                <div className="p-3.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-xl flex items-start space-x-3">
                    <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                    <div className="text-xs text-amber-900 dark:text-amber-200">
                        <p className="font-semibold mb-0.5">Estado de las Credenciales de Azure</p>
                        <p>{errorMessage}</p>
                    </div>
                </div>
            )}

            {/* Grid 2 Columnas de Tarjetas de Gestión */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* TARJETA 1: Estado de Cuenta & Contraseña */}
                <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            <h4 className="font-semibold text-gray-900 dark:text-white">Cuenta & Contraseña</h4>
                        </div>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${data?.accountEnabled !== false ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                            {data?.accountEnabled !== false ? 'Cuenta Habilitada' : 'Cuenta Inactiva'}
                        </span>
                    </div>

                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-xs space-y-1.5">
                        <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">User Principal Name:</span>
                            <span className="font-mono text-gray-800 dark:text-gray-200 font-semibold">{data?.userPrincipalName}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">Dominio de Correo:</span>
                            <span className="text-gray-800 dark:text-gray-200">{data?.mail || userEmail}</span>
                        </div>
                    </div>

                    <div className="pt-2 flex flex-wrap gap-2">
                        <button
                            onClick={() => setShowPasswordModal(true)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-all flex items-center space-x-1.5"
                        >
                            <Key className="w-3.5 h-3.5" />
                            <span>Cambiar / Resetear Contraseña</span>
                        </button>
                    </div>
                </div>

                {/* TARJETA 2: MFA y Sesiones Activas */}
                <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <ShieldAlert className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                            <h4 className="font-semibold text-gray-900 dark:text-white">MFA & Sesiones Activas</h4>
                        </div>
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                            {data?.mfa?.configured ? 'MFA Configurado' : 'MFA Pendiente'}
                        </span>
                    </div>

                    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-xs space-y-2">
                        <p className="text-gray-500 dark:text-gray-400 font-medium">Métodos de Autenticación Registrados:</p>
                        <div className="flex flex-wrap gap-1.5">
                            {(data?.mfa?.methods || ['Authenticator App']).map((method, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/50 rounded-lg text-[11px]">
                                    {method.includes('Authenticator') ? '📱 Authenticator App' : method.includes('phone') ? '📞 Teléfono SMS' : method}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="pt-2 flex flex-wrap gap-2">
                        <button
                            onClick={() => handleResetMfa(true)}
                            disabled={actionLoading}
                            className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-all flex items-center space-x-1.5 disabled:opacity-50"
                        >
                            <RefreshCw className="w-3.5 h-3.5" />
                            <span>Revocar Sesiones & Reestablecer MFA</span>
                        </button>
                    </div>
                </div>

                {/* TARJETA 3: Uso del Buzón (%) y Opciones */}
                <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <HardDrive className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                            <h4 className="font-semibold text-gray-900 dark:text-white">Almacenamiento del Buzón</h4>
                        </div>
                        {storageDisponible && (
                            <span className="text-xs font-mono font-bold text-purple-600 dark:text-purple-400">
                                {usagePct}% en uso
                            </span>
                        )}
                    </div>

                    {/* Barra de Progreso / Estado No Disponible */}
                    {storageDisponible ? (
                        <div className="space-y-1.5">
                            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                                <span>{usageGB} GB de {quotaGB} GB utilizados</span>
                                <span>{(quotaGB - usageGB).toFixed(1)} GB libres</span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 h-3 rounded-full overflow-hidden p-0.5">
                                <div
                                    className={`h-full rounded-full transition-all duration-500 ${usagePct > 85 ? 'bg-red-500' : usagePct > 65 ? 'bg-amber-500' : 'bg-purple-600'}`}
                                    style={{ width: `${usagePct}%` }}
                                ></div>
                            </div>
                        </div>
                    ) : (
                        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-xs text-gray-500 dark:text-gray-400 flex items-start space-x-2">
                            <Info className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>No disponible. {mb.storageUnavailableReason || 'Requiere un permiso adicional en Azure.'}</span>
                        </div>
                    )}

                    <div className="pt-2 flex flex-wrap gap-2">
                        <button
                            onClick={() => setShowForwardingModal(true)}
                            title="No disponible: requiere el permiso Mail.ReadWrite en Azure (no otorgado)"
                            className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-xs font-semibold rounded-xl transition-all flex items-center space-x-1.5"
                        >
                            <ArrowRightLeft className="w-3.5 h-3.5 text-blue-600" />
                            <span>Configurar Reenvío</span>
                            <span className="text-[10px] font-normal text-amber-600 dark:text-amber-400">(no disponible)</span>
                        </button>

                        <button
                            onClick={() => setShowOooModal(true)}
                            className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-xs font-semibold rounded-xl transition-all flex items-center space-x-1.5"
                        >
                            <Palmtree className="w-3.5 h-3.5 text-amber-600" />
                            <span>Fuera de Oficina</span>
                        </button>

                        <button
                            onClick={handleConvertSharedMailbox}
                            title="No disponible: es una operación exclusiva de Exchange Online, sin equivalente en Microsoft Graph"
                            className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-xs font-semibold rounded-xl transition-all flex items-center space-x-1.5"
                        >
                            <Mail className="w-3.5 h-3.5 text-purple-600" />
                            <span>Convertir a Buzón Compartido</span>
                            <span className="text-[10px] font-normal text-amber-600 dark:text-amber-400">(no disponible)</span>
                        </button>
                    </div>
                </div>

                {/* TARJETA 4: Alias de Correo Electrónico */}
                <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <Mail className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                            <h4 className="font-semibold text-gray-900 dark:text-white">Alias de Correo (proxyAddresses)</h4>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {data?.aliases?.length || 0} alias activos
                        </span>
                    </div>

                    {/* Lista de Alias */}
                    <div className="space-y-2 max-h-36 overflow-y-auto pr-1">
                        {(data?.aliases || []).map((alias, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-xs">
                                <span className="font-mono text-gray-800 dark:text-gray-200">{alias}</span>
                                <button
                                    onClick={() => handleRemoveAlias(alias)}
                                    className="text-gray-400 hover:text-red-600 p-1 rounded transition-colors"
                                    title="Eliminar alias"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Formulario Agregar Alias */}
                    <form onSubmit={handleAddAlias} className="flex items-center space-x-2 pt-2">
                        <input
                            type="email"
                            placeholder="nuevo.alias@dominio.cl"
                            value={newAlias}
                            onChange={(e) => setNewAlias(e.target.value)}
                            className="flex-1 px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                        />
                        <button
                            type="submit"
                            disabled={actionLoading || !newAlias.trim()}
                            className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-xl transition-all disabled:opacity-50 flex items-center space-x-1"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            <span>Agregar</span>
                        </button>
                    </form>
                </div>
            </div>

            {/* TARJETA 5 (Full Width): Licencias O365 y Grupos de Distribución */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Licencias O365 */}
                <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm space-y-3">
                    <div className="flex items-center space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <h4 className="font-semibold text-gray-900 dark:text-white">Licencias Microsoft 365 Asignadas</h4>
                    </div>

                    <div className="space-y-2">
                        {(data?.licenses || []).map((lic, idx) => (
                            <div key={idx} className="p-3 bg-blue-50/60 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 rounded-xl flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-semibold text-blue-950 dark:text-blue-200">{lic.name}</p>
                                    <p className="text-[11px] text-blue-700 dark:text-blue-300 font-mono">{lic.skuPartNumber}</p>
                                </div>
                                <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 rounded-md text-[10px] font-semibold">
                                    Asignada
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Grupos de Distribución y Teams */}
                <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm space-y-3">
                    <div className="flex items-center space-x-2">
                        <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        <h4 className="font-semibold text-gray-900 dark:text-white">Grupos & Microsoft Teams</h4>
                    </div>

                    <div className="space-y-2 max-h-48 overflow-y-auto">
                        {(data?.groups || []).map((group, idx) => (
                            <div key={idx} className="p-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-xl flex items-center justify-between text-xs">
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">{group.displayName}</p>
                                    {group.mail && <p className="text-[11px] text-gray-500 dark:text-gray-400 font-mono">{group.mail}</p>}
                                </div>
                                {group.isTeams ? (
                                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 rounded text-[10px] font-semibold">
                                        Teams
                                    </span>
                                ) : (
                                    <span className="px-2 py-0.5 bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300 rounded text-[10px]">
                                        Grupo
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

            </div>

            {/* MODAL: CAMBIAR CONTRASEÑA */}
            {showPasswordModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-100 dark:border-gray-700">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center space-x-2">
                            <Key className="w-5 h-5 text-blue-600" />
                            <span>Cambiar Contraseña O365 / AD</span>
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                            Se actualizará la clave para <strong>{userEmail}</strong>.
                        </p>

                        <form onSubmit={handleChangePassword} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Nueva Contraseña Temporaria
                                </label>
                                <input
                                    type="text"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Ingresa la nueva clave..."
                                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                />
                            </div>

                            <label className="flex items-center space-x-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={forceChangePass}
                                    onChange={(e) => setForceChangePass(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300"
                                />
                                <span>Forzar cambio de clave en el próximo inicio de sesión</span>
                            </label>

                            <div className="flex justify-end space-x-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowPasswordModal(false)}
                                    className="px-4 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={actionLoading}
                                    className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow transition-all"
                                >
                                    Guardar Nueva Clave
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: REENVÍO DE CORREO */}
            {showForwardingModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-100 dark:border-gray-700">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center space-x-2">
                            <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                            <span>Configurar Reenvío de Correo</span>
                        </h3>

                        <form onSubmit={handleSaveForwarding} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Correo de Destino para Reenvío
                                </label>
                                <input
                                    type="email"
                                    value={forwardingEmail}
                                    onChange={(e) => setForwardingEmail(e.target.value)}
                                    placeholder="reemplazo@bunzl.cl (dejar vacío para desactivar)"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                />
                            </div>

                            <div className="flex justify-end space-x-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowForwardingModal(false)}
                                    className="px-4 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={actionLoading}
                                    className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow transition-all"
                                >
                                    Guardar Reenvío
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: FUERA DE OFICINA */}
            {showOooModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-100 dark:border-gray-700">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center space-x-2">
                            <Palmtree className="w-5 h-5 text-amber-600" />
                            <span>Mensaje Fuera de Oficina (Auto-Reply)</span>
                        </h3>

                        <form onSubmit={handleSaveOoo} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Mensaje Automático de Vacaciones
                                </label>
                                <textarea
                                    rows={3}
                                    value={oooMessage}
                                    onChange={(e) => setOooMessage(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:outline-none"
                                />
                            </div>

                            <div className="flex justify-end space-x-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowOooModal(false)}
                                    className="px-4 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={actionLoading}
                                    className="px-4 py-2 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-xl shadow transition-all"
                                >
                                    Activar Auto-Respuesta
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
